import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { X, Save, FileText, Eye, Pencil } from 'lucide-react';
import Markdown from './Markdown';

const STARTER = `Welcome to the API. This guide gets you making your first request.

## Base URL

All requests are made against:

\`\`\`
https://api.example.com
\`\`\`

## Your first request

\`\`\`bash
curl "https://api.example.com/v1/ping" \\
  -H "Authorization: Bearer YOUR_API_KEY"
\`\`\`

> Keep your API key secret — never commit it to source control.
`;

export default function DocPageEditorModal({ projectId, page, sections = [], onClose, onSaved }) {
  const isNew = !page;
  const [form, setForm] = useState({
    title: page?.title || '',
    section: page?.section || 'Getting Started',
    content: page?.content ?? (isNew ? STARTER : ''),
    is_published: page ? !!page.is_published : true,
  });
  const [tab, setTab] = useState('write');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save(e) {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const res = isNew
        ? await api.post(`/projects/${projectId}/doc-pages`, form)
        : await api.put(`/doc-pages/${page.id}`, form);
      toast.success(isNew ? 'Page created' : 'Page saved');
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save page');
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <div style={sty.header}>
          <div style={sty.headerLeft}>
            <FileText size={18} style={{ color: 'var(--accent)' }} />
            <h3 className="modal-title" style={{ margin: 0 }}>{isNew ? 'New Documentation Page' : 'Edit Page'}</h3>
          </div>
          <button style={sty.closeBtn} onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={save}>
          <div style={sty.row}>
            <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
              <label className="form-label">Title *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Introduction" required />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Section</label>
              <input list="doc-sections" value={form.section} onChange={e => set('section', e.target.value)} placeholder="Getting Started" />
              <datalist id="doc-sections">
                {sections.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <div style={sty.contentHead}>
              <label className="form-label" style={{ margin: 0 }}>Content</label>
              <div style={sty.tabs}>
                <button type="button" style={{ ...sty.tab, ...(tab === 'write' ? sty.tabOn : {}) }} onClick={() => setTab('write')}>
                  <Pencil size={11} /> Write
                </button>
                <button type="button" style={{ ...sty.tab, ...(tab === 'preview' ? sty.tabOn : {}) }} onClick={() => setTab('preview')}>
                  <Eye size={11} /> Preview
                </button>
              </div>
            </div>

            {tab === 'write' ? (
              <textarea value={form.content} onChange={e => set('content', e.target.value)} rows={16} className="mono"
                style={sty.editor} placeholder="Write your guide in Markdown…" />
            ) : (
              <div style={sty.preview}>
                {form.content.trim()
                  ? <Markdown content={form.content} />
                  : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Nothing to preview yet.</span>}
              </div>
            )}
            <div style={sty.hint}>
              Markdown: <code style={sty.code}># heading</code>, <code style={sty.code}>**bold**</code>,
              {' '}<code style={sty.code}>`code`</code>, <code style={sty.code}>```blocks```</code>, lists, tables, links.
            </div>
          </div>

          <label style={sty.checkRow}>
            <input type="checkbox" checked={form.is_published} onChange={e => set('is_published', e.target.checked)} />
            <span>
              <span style={{ fontSize: 13 }}>Visible in published docs</span>
              <span style={sty.checkHint}>Uncheck to keep this page as an internal draft.</span>
            </span>
          </label>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={13} /> {saving ? 'Saving…' : isNew ? 'Create Page' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const sty = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 5 },
  row: { display: 'flex', gap: 12 },
  contentHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  tabs: { display: 'flex', gap: 4 },
  tab: {
    display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--border)',
    color: 'var(--text-2)', borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
  },
  tabOn: { background: 'var(--bg-active)', color: 'var(--text-1)', borderColor: 'var(--border-light)' },
  editor: { width: '100%', resize: 'vertical', fontSize: 12.5, lineHeight: 1.6 },
  preview: {
    border: '1px solid var(--border)', borderRadius: 8, padding: 16, minHeight: 260,
    maxHeight: 420, overflowY: 'auto', background: 'var(--bg-base)',
  },
  hint: { fontSize: 11, color: 'var(--text-muted)', marginTop: 6 },
  code: { fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 11 },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 16, cursor: 'pointer' },
  checkHint: { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
};
